import ArrowUpRight16 from '@carbon/icons-react/es/arrow--up-right/16';
import ChevronRight16 from '@carbon/icons-react/es/chevron--right/16';
import { Layout, Skeleton } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { Comment, UserMessage } from 'api/conversations';
import { ProjectId } from 'api/models';
import { PinResourceType, PinScopeType } from 'api/spotlight/types';
import { SpotlightGroup } from 'components/organisms/spotlight/spotlight-in-tag';
import { useToggleSpotlight } from 'hooks/use-spotlight';
import { useActiveGlobalConversation } from '../../proj-conversation-hooks';
import {
  pinScopeTypeFromProjectConversation,
  scopeIdFromProjectConversation,
} from '../../spotlight-utils';
import { useOpenConversationItem } from '../channel-details';
import { ConversationIcon } from '../conv-item';
import { UpdateConversationModal } from '../conversation-form';
import { ProjectGlobalConversationPane } from './global-conversation-pane';
import {
  ChannelName,
  Content,
  ConversationHeader,
  HeaderContent,
  HeaderContentPart,
  PrimaryHeader,
  SecondaryHeader,
} from './styles';

interface Props {
  projectId: ProjectId;
}

export const ProjectGlobalConversationPaneWrapper: FC<Props> = ({
  projectId,
}) => {
  const { activeConversation, loading } = useActiveGlobalConversation();
  const [isUpdateDetailsModalVisible, setUpdateConversationModalVisibility] =
    useState(false);
  const onOpen = useOpenConversationItem(projectId, activeConversation);
  const { pinScopeIdForConversation, pinScopeTypeForConversation } = useMemo(
    () => ({
      pinScopeIdForConversation: activeConversation
        ? scopeIdFromProjectConversation(activeConversation)
        : undefined,
      pinScopeTypeForConversation: activeConversation
        ? pinScopeTypeFromProjectConversation(activeConversation)
        : PinScopeType.ProjectConversation,
    }),
    [activeConversation],
  );

  const { spotlights, onSpotlightToggle } = useToggleSpotlight({
    resourceType: PinResourceType.Chat,
    scopeId: pinScopeIdForConversation,
    scopeType: pinScopeTypeForConversation,
  });
  const onStarMessage = useCallback(
    async (comment: Comment) => {
      if (!comment.commentId) {
        return;
      }

      onSpotlightToggle(
        comment.commentId,
        pinScopeIdForConversation,
        (comment as UserMessage).content,
      );
    },
    [onSpotlightToggle, pinScopeIdForConversation],
  );

  if (loading || !activeConversation) {
    return <Skeleton title={false} />;
  }

  return (
    <Layout.Content>
      <Content>
        <HeaderContent private={activeConversation.private}>
          <PrimaryHeader>
            <ConversationHeader
              private={activeConversation.private}
              onClick={onOpen}
              data-cy="conversation-header"
            >
              <ConversationIcon
                conversation={activeConversation}
                staticIcon={false}
              />
              <ChannelName>{activeConversation?.entityName}</ChannelName>
              {activeConversation.displayEntityType === 'tasks' ? (
                <ChevronRight16 />
              ) : (
                <ArrowUpRight16 />
              )}
            </ConversationHeader>
            <HeaderContentPart>
              <UpdateConversationModal
                isVisible={isUpdateDetailsModalVisible}
                onCancel={() => setUpdateConversationModalVisibility(false)}
              />
            </HeaderContentPart>
          </PrimaryHeader>
          {(spotlights ?? []).length > 0 ? (
            <SecondaryHeader>
              <SpotlightGroup
                spotlights={spotlights}
                onSpotlightClick={(s) => {
                  document
                    .querySelector(`[data-spotlight='${s?.resourceId}']`)
                    ?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'nearest',
                      inline: 'nearest',
                    });
                }}
              />
            </SecondaryHeader>
          ) : null}
        </HeaderContent>
        <ProjectGlobalConversationPane
          projectId={projectId}
          isUserMemberOfChannel
          spotlights={spotlights ?? []}
          onSpolightToggle={onStarMessage}
        />
      </Content>
    </Layout.Content>
  );
};
